import React, { useState, useEffect, useCallback } from 'react';
import { ConfigDocument } from '../types';
import { getConfig, updateConfig } from '../services/configApi';
import S3BucketsTab from '../components/config/S3BucketsTab';
import SMBDestinationsTab from '../components/config/SMBDestinationsTab';
import CredentialsTab from '../components/config/CredentialsTab';
import PressConfigTab from '../components/config/PressConfigTab';
import DigitalDestinationsTab from '../components/config/DigitalDestinationsTab';
import AlertsTab from '../components/config/AlertsTab';

type TabId = 's3' | 'smb' | 'credentials' | 'press' | 'digital' | 'alerts';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 's3', label: 'S3 Buckets' },
  { id: 'smb', label: 'SMB Destinations' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'press', label: 'Press Configuration' },
  { id: 'digital', label: 'Digital Destinations' },
  { id: 'alerts', label: 'Alerts' },
];

const ConfigurationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('s3');
  const [config, setConfig] = useState<ConfigDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConfig();
      setConfig(data);
    } catch (err: any) {
      setError(err.message || 'Unable to load configuration. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async (updatedConfig: ConfigDocument) => {
    const saved = await updateConfig(updatedConfig);
    setConfig(saved);
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Configuration</h1>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
            <span>{error}</span>
            <button
              onClick={loadConfig}
              className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}

        {/* Tab navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Configuration tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-selected={activeTab === tab.id}
                role="tab"
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content panels — all rendered, only active is visible (preserves state) */}
        <div style={{ display: activeTab === 's3' ? 'block' : 'none' }} role="tabpanel">
          <S3BucketsTab config={config} onSave={handleSave} loading={loading} />
        </div>
        <div style={{ display: activeTab === 'smb' ? 'block' : 'none' }} role="tabpanel">
          <SMBDestinationsTab config={config} onSave={handleSave} loading={loading} />
        </div>
        <div style={{ display: activeTab === 'credentials' ? 'block' : 'none' }} role="tabpanel">
          <CredentialsTab config={config} onSave={handleSave} loading={loading} />
        </div>
        <div style={{ display: activeTab === 'press' ? 'block' : 'none' }} role="tabpanel">
          <PressConfigTab config={config} onSave={handleSave} onReload={loadConfig} loading={loading} />
        </div>
        <div style={{ display: activeTab === 'digital' ? 'block' : 'none' }} role="tabpanel">
          <DigitalDestinationsTab config={config} onSave={handleSave} loading={loading} />
        </div>
        <div style={{ display: activeTab === 'alerts' ? 'block' : 'none' }} role="tabpanel">
          <AlertsTab config={config} onSave={handleSave} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;
